import { PHONE_INPUT_TYPE, SELECT_INPUT_TYPE, VALIDATION_ERROR_MESSAGE, Yup } from "@mavichpe/dynamic-forms";
import { Button } from "reactstrap";
import { OTHER_CHARGE_THIRD_PARTY_ID_TYPES } from "@utils/Constants";
import {
    OTHER_CHARGE_NOMBRE_TERCERO_MAX_LENGTH,
    THIRD_PARTY_NAME_LENGTH_MESSAGE,
    isValidThirdPartyName,
} from "@utils/DocumentOtherCharges";
import { withIdentificationFormat, withPadronRegistration } from "@utils/Forms";
import { IDENTIFICATION_MAX_LENGTH } from "@utils/Identification";
import { OpenSearchClientByIdentificationPubSub } from "@utils/pubsubs/PubSub";

/**
 * El tipo y numero de identificacion son requeridos porque viajan como
 * IdentificacionTercero en el cargo "04" del XML: un proveedor sin ellos no se puede
 * usar para facturar un envio. Se usa OTHER_CHARGE_THIRD_PARTY_ID_TYPES y no
 * TIPOS_DE_IDENTIFICACION porque a ese mapa le falta el NITE, que el XSD si admite aqui.
 *
 * La identificacion se valida en dos niveles: el formato de la nota 4 del anexo, y la
 * inscripcion ante Tributacion. Un proveedor con una cedula no inscrita hace que Hacienda
 * rechace con el codigo -105 cada factura que le cobre un envio.
 *
 * El nombre tampoco es libre: sale como NombreTercero, que el XSD limita a entre 5 y 100
 * caracteres. Los limites se importan de `DocumentOtherCharges.ts` para no repetir aqui un
 * numero que ya esta derivado del XSD.
 */
const ShippingProviderFormConfig = {
    sections: [
        {
            hideBorderBottom: true,
            "fields": [
                [
                    {
                        "type": "checkbox",
                        "name": "active",
                        "label": "Proveedor Activo",
                        "sizing": {
                            "sm": {
                                "offset": 2
                            }
                        },
                        "defaultValue": true
                    }
                ],
                [
                    {
                        "type": "number",
                        "name": "codigo",
                        "label": "Codigo",
                        "isHorizontal": true,
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 2
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                ],
                [
                    {
                        "type": "text",
                        "name": "nombre",
                        "label": "Nombre del Proveedor",
                        "isHorizontal": true,
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 8
                        },
                        "maxlength": OTHER_CHARGE_NOMBRE_TERCERO_MAX_LENGTH,
                        "validation": Yup.string()
                            .required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR)
                            // Se mide sobre el nombre recortado, que es el que viaja en el XML:
                            // `.min()` de Yup contaria los espacios y dejaria pasar "FABA ".
                            .test(
                                "nombre-tercero-largo",
                                THIRD_PARTY_NAME_LENGTH_MESSAGE,
                                (value) => isValidThirdPartyName(value),
                            ),
                    },
                ],
                [
                    {
                        "type": SELECT_INPUT_TYPE,
                        "name": "tipo_identificacion",
                        "label": "Tipo de Identificacion",
                        "isHorizontal": true,
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 4
                        },
                        "options": OTHER_CHARGE_THIRD_PARTY_ID_TYPES,
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                ],
                [
                    {
                        "type": "text",
                        "name": "identificacion",
                        "label": "Identificacion",
                        "isHorizontal": true,
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 4
                        },
                        "maxlength": IDENTIFICATION_MAX_LENGTH,
                        "validation": withPadronRegistration(
                            withIdentificationFormat(
                                Yup.string()
                                    .required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR)
                                    .max(IDENTIFICATION_MAX_LENGTH, `La identificacion no puede exceder ${IDENTIFICATION_MAX_LENGTH} caracteres`)
                            )
                        ),
                        "suffixButton": true,
                        "suffix": <Button
                            block
                            className="rounded-0"
                            color="primary"
                            type="button"
                            onClick={() => OpenSearchClientByIdentificationPubSub.publish({ openOffCanvas: true })}>
                            Buscar
                        </Button>
                    },
                ],
                [
                    {
                        "type": PHONE_INPUT_TYPE,
                        "name": "telefono",
                        "label": "Telefono",
                        "isHorizontal": true,
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 4
                        },
                    },
                ],
                [
                    {
                        "type": "text",
                        "name": "email",
                        "label": "Correo Electronico",
                        "isHorizontal": true,
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 6
                        },
                        "validation": Yup.string().email("El correo electronico no es valido").notRequired(),
                    },
                ],
                [
                    {
                        "type": "money",
                        "name": "default_amount",
                        "label": "Costo de Envio Sugerido",
                        "isHorizontal": true,
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 3
                        },
                    },
                ],
                [
                    {
                        "type": "text",
                        "name": "notas",
                        "label": "Notas",
                        "isHorizontal": true,
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 8
                        },
                    },
                ],
            ]
        }
    ],
};

export default ShippingProviderFormConfig;
